import type { Nullable, Optional, Undefinable } from '@zen/utils/typescript';

export type StepDirection = -1 | 1;

export interface StepConfig {
  max?: number | string;
  min?: number | string;
  step?: number | string;
}

const parseNumericValue = (value: Optional<number | string>): Nullable<number> => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedValue: number = typeof value === 'number' ? value : parseFloat(value);

  return Number.isNaN(parsedValue) ? null : parsedValue;
};

export const getStepPrecision = (step: number): number => {
  const [, decimalDigits = '']: string[] = String(step).split('.');

  return decimalDigits.length;
};

export const canStep = (value: Optional<number | string>, direction: StepDirection, config: StepConfig): boolean => {
  const currentValue: Nullable<number> = parseNumericValue(value);

  if (currentValue === null) {
    return true;
  }

  const bound: Nullable<number> = parseNumericValue(direction === 1 ? config.max : config.min);

  if (bound === null) {
    return true;
  }

  return direction === 1 ? currentValue < bound : currentValue > bound;
};

export const stepValue = (value: Optional<number | string>, direction: StepDirection, config: StepConfig): number => {
  const step: number = parseNumericValue(config.step) ?? 1;
  const min: Nullable<number> = parseNumericValue(config.min);
  const max: Nullable<number> = parseNumericValue(config.max);
  const currentValue: Nullable<number> = parseNumericValue(value);

  if (currentValue === null) {
    return min ?? direction * step;
  }

  const scale: number = 10 ** getStepPrecision(step);
  const nextValue: number = Math.round((currentValue + direction * step) * scale) / scale;
  const clampedToMin: number = min === null ? nextValue : Math.max(min, nextValue);

  return max === null ? clampedToMin : Math.min(max, clampedToMin);
};

export const prepareInputDigitsPattern = (maxDecimalDigits: Undefinable<number>, maxWholeDigits: Undefinable<number>): RegExp => {
  const decimalDigitsPattern: string = `[,.][0-9]${maxDecimalDigits ? `{0,${maxDecimalDigits}}` : '*'}`;
  const wholeDigitsPattern: string = `[0-9]${maxWholeDigits ? `{0,${maxWholeDigits}}` : '*'}`;

  return new RegExp(`^(${wholeDigitsPattern})(${decimalDigitsPattern})?$`);
};

export const getInputSymbols = (maxDecimalDigits: Undefinable<number>, min: Undefinable<number | string>): string[] => {
  const decimalSymbol: string = maxDecimalDigits === 0 ? '.' : '';
  const negativeNumberSymbol: string = typeof min === 'number' && min >= 0 ? '-' : '';

  return ['+', decimalSymbol, negativeNumberSymbol];
};

export const getPlaceholder = (maxDecimalDigits: Undefinable<number>, placeholder: Undefinable<string>): string => {
  const defaultPlaceholder: string = maxDecimalDigits === 0 ? '0' : '0.00';

  return placeholder || defaultPlaceholder;
};
